import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { LessonTemplate, LessonTemplateField } from '@/types';

export function useLessonTemplates() {
  const [templates, setTemplates] = useState<LessonTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('lesson_templates')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setTemplates((data as LessonTemplate[]) ?? []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const byField = useMemo(() => {
    const map: Record<string, LessonTemplate[]> = {};
    templates.forEach((t) => {
      if (!map[t.field]) map[t.field] = [];
      map[t.field].push(t);
    });
    return map;
  }, [templates]);

  const addTemplate = useCallback(
    async (field: LessonTemplateField, content: string) => {
      const value = content.trim();
      if (!value) return;
      const { data: userData } = await supabase.auth.getUser();
      const ownerId = userData.user?.id;
      if (!ownerId) return;
      const { data, error } = await supabase
        .from('lesson_templates')
        .insert({ owner_id: ownerId, field, content: value })
        .select()
        .maybeSingle();
      if (error) throw error;
      if (data) setTemplates((prev) => [data as LessonTemplate, ...prev]);
    },
    [],
  );

  const removeTemplate = useCallback(async (id: string) => {
    const { error } = await supabase.from('lesson_templates').delete().eq('id', id);
    if (error) throw error;
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return { templates, byField, loading, addTemplate, removeTemplate, reload };
}